import { useEffect, useState } from "react";
import { api, apiError, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2, ChevronDown, Tag } from "lucide-react";

const TYPES = [
  { value: "azienda", label: "Azienda" },
  { value: "privato", label: "Privato" },
  { value: "pa", label: "Pubblica amministrazione" },
  { value: "condominio", label: "Condominio" },
];

export default function Listini() {
  const { user } = useAuth();
  const readOnly = user?.role !== "azienda";
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [itemFor, setItemFor] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = () => api.get("/price-lists").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/users", { params: { role: "agente" } }).then(({ data }) => setAgents(data)).catch(() => {});
    api.get("/products").then(({ data }) => setProducts(data));
  }, []);

  const fields = [
    { name: "name", label: "Nome listino", required: true, full: true },
    { name: "customer_type", label: "Tipologia cliente", type: "select", options: TYPES },
    {
      name: "agent_id", label: "Riservato all'agente", type: "select",
      options: agents.map((a) => ({ value: a.id, label: a.name })),
    },
    { name: "discount_pct", label: "Sconto standard %", type: "number" },
    { name: "active", label: "Attivo", type: "switch", hint: "Applicabile ai nuovi preventivi" },
    { name: "notes", label: "Note", type: "textarea", full: true },
  ];

  const itemFields = [
    {
      name: "product_id", label: "Articolo", type: "select", required: true, full: true,
      options: products.map((p) => ({ value: p.id, label: `${p.code} — ${p.name} (base ${eur(p.price)})` })),
    },
    { name: "price", label: "Prezzo dedicato €", type: "number", required: true },
  ];

  const save = async (values) => {
    try {
      if (editing) await api.put(`/price-lists/${editing.id}`, values);
      else await api.post("/price-lists", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const addItem = async (values) => {
    try {
      await api.post(`/price-lists/${itemFor.id}/items`, values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="listini-page">
      <PageHeader title="Listini e sconti" subtitle={`${rows.length} listini`}>
        {!readOnly && (
          <Button className="h-11 rounded-sm" onClick={() => { setEditing(null); setOpen(true); }}
                  data-testid="new-pricelist-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuovo listino
          </Button>
        )}
      </PageHeader>

      <div className="space-y-4" data-testid="listini-list">
        {rows.map((pl) => (
          <Card key={pl.id} className="rounded-sm shadow-none overflow-hidden">
            <button type="button" className="w-full text-left px-4 sm:px-6 py-4 flex items-center gap-3"
                    onClick={() => setExpanded(expanded === pl.id ? null : pl.id)}
                    data-testid={`expand-pricelist-${pl.id}`}>
              <Tag className="h-4 w-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold">{pl.name}</p>
                  <Badge variant="secondary" className="rounded-sm capitalize">
                    {TYPES.find((t) => t.value === pl.customer_type)?.label || "Tutte le tipologie"}
                  </Badge>
                  {pl.agent_name && <Badge variant="outline" className="rounded-sm">Agente {pl.agent_name}</Badge>}
                  {!pl.active && <Badge variant="outline" className="rounded-sm">non attivo</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Sconto standard {pl.discount_pct || 0}% ·{" "}
                  {(pl.items?.length || 0) === 1 ? "1 prezzo dedicato" : `${pl.items?.length || 0} prezzi dedicati`}
                </p>
              </div>
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded === pl.id ? "rotate-180" : ""}`} />
            </button>

            {expanded === pl.id && (
              <div className="border-t border-border px-4 sm:px-6 py-4" data-testid={`pricelist-detail-${pl.id}`}>
                <div className="divide-y divide-border">
                  {(pl.items || []).map((i) => (
                    <div key={i.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                      <div>
                        <p className="font-medium">
                          <span className="font-mono text-xs text-muted-foreground mr-2">{i.product_code}</span>
                          {i.product_name}
                        </p>
                        <p className="text-xs text-muted-foreground">Listino base {eur(i.list_price)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-semibold">{eur(i.price)}</span>
                        {!readOnly && (
                          <Button variant="ghost" size="icon" className="text-destructive"
                                  onClick={async () => { await api.delete(`/price-list-items/${i.id}`); load(); }}
                                  data-testid={`delete-item-${i.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(pl.items || []).length === 0 && (
                    <p className="py-2 text-sm text-muted-foreground">
                      Nessun prezzo dedicato: si applica il prezzo base con lo sconto standard.
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {readOnly ? (
                    <p className="text-xs text-muted-foreground">
                      Listino in sola lettura: si applica automaticamente ai tuoi preventivi.
                    </p>
                  ) : (
                    <>
                      <Button size="sm" className="rounded-sm" onClick={() => setItemFor(pl)}
                              data-testid={`add-item-${pl.id}`}>
                        <Plus className="h-3.5 w-3.5 mr-2" /> Prezzo dedicato
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm"
                              onClick={() => { setEditing(pl); setOpen(true); }} data-testid={`edit-pricelist-${pl.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                              onClick={async () => { await api.delete(`/price-lists/${pl.id}`); load(); }}
                              data-testid={`delete-pricelist-${pl.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </Card>
        ))}
        {rows.length === 0 && (
          <ListCard><Empty text="Nessun listino: crea listini per tipologia cliente o per agente." /></ListCard>
        )}
      </div>

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Modifica listino" : "Nuovo listino"}
                  fields={fields} initial={editing || { customer_type: "azienda", discount_pct: 0, active: true }}
                  onSubmit={save} testid="pricelist-dialog" />
      <FormDialog open={!!itemFor} onOpenChange={(v) => !v && setItemFor(null)} title="Prezzo dedicato"
                  fields={itemFields} initial={{}} onSubmit={addItem} testid="item-dialog" />
    </div>
  );
}
